/**
 * What every page beside the agents screen is built from: the page frame, an empty state, the bar of
 * tabs under the top bar, and a ticker tile.
 */
import type { ReactNode } from 'react'
import { create } from 'zustand'
import { fillComposer } from './Agents'
import { cx } from './kit'
import { useDemo } from './store'

export const Page = ({ children, className }: { children: ReactNode; className?: string }) => <div className={cx('w-full px-4 pb-10 pt-5 md:px-10 md:pt-8', className)}>{children}</div>

/** A page with nothing on it yet: what is missing, in a sentence, and the one way forward. */
export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center px-6 pb-[12vh] pt-10 text-center">
      {icon}
      <h1 className="m-0 mt-4 font-ws-serif text-[28px] font-normal leading-9 tracking-[-0.01em] text-ws-fg">{title}</h1>
      <p className="m-0 mt-3 max-w-[420px] text-[13px] leading-5 tracking-normal text-ws-fg-3">{children}</p>
      {action && <div className="mt-6 flex items-center gap-2">{action}</div>}
    </div>
  )
}

/** The bar under the top bar: tabs (or a way back) on the left, the page's own actions on the right. */
export function Bar({ children, right }: { children?: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-ws-line pl-2 pr-2 md:pr-4">
      <div className="flex min-w-0 flex-1 items-center">{children}</div>
      {right && <div className="flex shrink-0 items-center">{right}</div>}
    </div>
  )
}

/** What each page's tabs are set to (kept while you move between pages). */
export const useTabs = create<{ account: string; workers: string; apps: string; settings: string; set: (p: Record<string, unknown>) => void }>((set) => ({
  account: 'all',
  workers: 'running',
  apps: 'all',
  settings: 'general',
  set: (p) => set(p as never),
}))

/** Take words to an agent: open its empty page with them already in the composer. */
export const ask = (agentId: string | null | undefined, text: string) => {
  const s = useDemo.getState()
  const id = s.agents.some((a) => a.id === agentId) ? agentId! : (s.agents.find((a) => a.kind === 'lead')?.id ?? s.agents[0]?.id ?? '')
  if (!id) return
  s.openSession(id, null)
  window.setTimeout(() => fillComposer(text), 60)
}

/** A stock's mark where there is no logo: its letters on a tile. */
export const Ticker = ({ symbol }: { symbol: string }) => (
  <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-[6px] bg-ws-bg px-[6px] text-[10.5px] font-semibold leading-none tracking-[0.02em] text-ws-fg-2" aria-hidden>
    {symbol}
  </span>
)
